import { motion } from "framer-motion";
import { Film, Megaphone, LayoutGrid, ArrowUpRight } from "lucide-react";
import SectionLabel from "./SectionLabel";

const pillars = [
  {
    index: "01",
    icon: Film,
    title: "Post Production",
    kicker: "Make It Watchable",
    body:
      "The polished edit your conversation deserves. We cut, grade and package every episode so it feels premium end to end.",
    deliverables: [
      "Long form video and audio edits",
      "Episode trailers",
      "Thumbnails and title concepts",
      "Short form clips for Reels, Shorts, TikTok",
      "Chapters and timestamps",
    ],
  },
  {
    index: "02",
    icon: Megaphone,
    title: "Distribution",
    kicker: "Make It Travel",
    body:
      "Reach is not an afterthought. Every episode leaves with the marketing stack to show up everywhere your audience already lives.",
    deliverables: [
      "LinkedIn carousels and posts",
      "X threads and quote tweets",
      "Blog articles with SEO",
      "Weekly newsletter copy",
      "Guest promo kits",
    ],
  },
  {
    index: "03",
    icon: LayoutGrid,
    title: "Publishing & Ops",
    kicker: "Make It Run",
    body:
      "The behind the scenes workflow that keeps shows live, consistent and reportable without you touching a dashboard.",
    deliverables: [
      "Multi platform uploads and scheduling",
      "Show notes, descriptions and tags",
      "Transcripts and captions",
      "Monthly performance reports",
      "Asset library and version control",
    ],
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative px-6 md:px-16 lg:px-32 py-24 md:py-32 bg-[#070707] border-y border-[#1a1a1a]"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
        <div className="lg:col-span-7">
          <SectionLabel number="04" label="Services" testId="services-label" />
          <h2 className="font-display font-bold uppercase text-2xl md:text-4xl tracking-tighter leading-[0.95] [word-spacing:0.18em]">
            Three Pillars.<br />
            <span className="text-[#D4AF37]">One Partnership.</span>
          </h2>
        </div>
        <p className="lg:col-span-5 text-[#A3A3A3] text-sm md:text-base leading-relaxed">
          Everything after you hit stop record, owned by one team. Post production,
          distribution and the operational stack that keeps your show shipping and
          growing.
        </p>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1a1a1a] border border-[#1a1a1a]">
        {pillars.map((p, i) => {
          const Icon = p.icon;
          return (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`service-pillar-${i}`}
              className="group relative bg-[#0A0A0A] p-8 md:p-10 hover:bg-[#0D0D0D] transition-colors duration-500 flex flex-col gap-7"
            >
              <div className="flex items-start justify-between">
                <div className="w-14 h-14 border border-[#333] flex items-center justify-center group-hover:border-[#F4C75E] group-hover:text-[#F4C75E] transition-colors duration-300">
                  <Icon className="w-5 h-5" />
                </div>
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#666] group-hover:text-[#F4C75E] transition-colors">
                  Pillar / {p.index}
                </span>
              </div>

              <div className="space-y-3">
                <span className="inline-block text-[11px] uppercase tracking-[0.25em] text-[#F4C75E] font-bold">
                  {p.kicker}
                </span>
                <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight leading-[0.95] [word-spacing:0.18em]">
                  {p.title}
                </h3>
                <p className="text-sm md:text-base text-[#A3A3A3] leading-relaxed">
                  {p.body}
                </p>
              </div>

              <ul className="mt-2 space-y-2.5 pt-6 border-t border-[#1a1a1a]">
                {p.deliverables.map((d) => (
                  <li
                    key={d}
                    data-testid={`pillar-${i}-item`}
                    className="flex items-start gap-3 text-sm text-[#C9C9C9]"
                  >
                    <span aria-hidden className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F4C75E] shrink-0" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          );
        })}
      </div>

      <div className="max-w-7xl mx-auto mt-10">
        <a
          href="https://calendly.com/darshan081200/30min"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="services-cta"
          className="group inline-flex items-center gap-3 border border-[#F4C75E] text-[#F4C75E] px-6 py-4 text-xs md:text-sm uppercase tracking-[0.25em] font-bold hover:bg-[#F4C75E] hover:text-black transition-all duration-300"
        >
          Build Your Media Engine
          <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </a>
      </div>
    </section>
  );
}
